import type { IEmployeeApiPort } from '@personnel/application/ports/IEmployeeApiPort';
import type { EmployeeDeleteDto, EmployeeDeleteResponseDto } from '@personnel/application/dtos';

/**
 * Use Case for deleting an employee
 *
 * Orchestrates the employee deletion flow by delegating to the API port.
 * Follows the same pattern as other employee use cases.
 *
 * @param {Object} props - Use case dependencies
 * @param {IEmployeeApiPort} props.apiPort - Port for API operations
 * @returns {Object} Use case executor with execute method
 *
 * @example
 * const useCase = deleteEmployeeUseCase({ apiPort: employeeRepository });
 * const result = await useCase.execute({ userUuid: 'user-uuid-123' });
 * console.log(result.success, result.deletedUuid);
 */
export const deleteEmployeeUseCase = (props: { apiPort: IEmployeeApiPort }) => {
  const { apiPort } = props;

  /**
   * Executes the employee deletion
   *
   * @param {EmployeeDeleteDto} request - Employee deletion data
   * @returns {Promise<EmployeeDeleteResponseDto>} Deletion result with success status and deleted UUID
   */
  const execute = async (request: EmployeeDeleteDto): Promise<EmployeeDeleteResponseDto> => {
    return apiPort.deleteEmployee(request);
  };

  return { execute };
};
